import { useState, useEffect } from 'react';
import { Badge } from 'react-bootstrap';
import { FaSyncAlt, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import ClientEOIService from '../../../../services/ClientEOIService';
import AuthService from '../../../../services/AuthService';
import ContractService from '../../../../services/ContractService';

const useClientEOIDetail2 = ({ eoi, show, onClose, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [comments, setComments] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    // États pour Lightbox
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const currentUser = AuthService.getCurrentUser();

    useEffect(() => {
        if (eoi) {
            setComments(eoi.comments || '');
            setRejectionReason(eoi.rejectionReason || '');
        }
    }, [eoi]);

    // ✅ Récupération de l'URL du média (backend uniquement)
    const getMediaUrl = (media) => {
        return media.urlPresigned || media.url;
    };

    // ✅ Préparer les images pour Lightbox
    const prepareLightboxImages = (eoi) => {
        const images = [];

        // Ajouter l'image principale du service (première image ou image par défaut)
        if (eoi.mediaSubscriptions && eoi.mediaSubscriptions.length > 0) {
            const mainMedia = eoi.mediaSubscriptions[0];
            images.push({
                src: getMediaUrl(mainMedia),
                alt: mainMedia.name || 'Image du service'
            });

            // Ajouter les autres images
            eoi.mediaSubscriptions.slice(1).forEach(media => {
                images.push({
                    src: getMediaUrl(media),
                    alt: media.name || 'Image'
                });
            });
        } else {
            // Image par défaut si aucune image n'est disponible
            images.push({
                src: "/assets/images/default-service.jpg",
                alt: 'Service par défaut'
            });
        }

        return images;
    };

    // ✅ Gestion Lightbox
    const handleOpenLightbox = (images, index = 0) => {
        setLightboxImages(images);
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const handleCloseLightbox = () => {
        setLightboxOpen(false);
        setLightboxImages([]);
        setLightboxIndex(0);
    };

    // ✅ Gestion de la fermeture de la modal
    const onCloseHandler = () => {
        if (isEditing) {
            Swal.fire({
                title: 'Confirmation',
                text: 'Vous avez des modifications non sauvegardées. Voulez-vous vraiment fermer ?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Oui, fermer',
                cancelButtonText: 'Annuler'
            }).then((result) => {
                if (result.isConfirmed) {
                    setIsEditing(false);
                    setComments(eoi.comments || '');
                    onClose();
                }
            });
        } else {
            onClose();
        }
    };

    const handleSave = async () => {
        if (!eoi || !currentUser) {
            Swal.fire('Erreur', 'Données manquantes pour la mise à jour', 'error');
            return;
        }

        setIsLoading(true);
        try {
            const updateData = {
                comments: comments,
                clientId: eoi.client?.id || eoi.clientId,
                professionalId: eoi.professional?.id || eoi.professionalId,
                lastModifiedBy: currentUser.username,
                clientEOIStatus: "PENDING",
            };

            const updatedEOI = await ClientEOIService.updateClientEOI(eoi.id, updateData);
            onUpdate(updatedEOI);
            setIsEditing(false);
            Swal.fire('Succès !', 'Votre demande a été mise à jour.', 'success');
        } catch (error) {
            console.error('Error updating EOI:', error);
            Swal.fire('Erreur !', 'Une erreur est survenue lors de la mise à jour.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const createContractFromEOI = async (eoi) => {
        try {
            const username = currentUser?.username || 'manager';
            const currentDate = new Date().toISOString().split('T')[0];

            const startDate = new Date(currentDate);
            const endDate = new Date(startDate);
            endDate.setMonth(startDate.getMonth() + (eoi.contractDuration || 1));

            const professional = eoi.professional;
            const serviceName = professional?.selectedService || eoi.serviceName || 'Service non spécifié';

            const subServices = professional?.subServices;
            const subServiceName = subServices ?
                (subServices.split(';')[0] || subServices.split(',')[0] || subServices) :
                'Sous-service non spécifié';

            const getFrequency = () => {
                if (serviceName.includes('quotidien') || serviceName.includes('daily')) return 'Quotidienne';
                if (serviceName.includes('hebdomadaire') || serviceName.includes('weekly')) return 'Hebdomadaire';
                if (serviceName.includes('mensuelle') || serviceName.includes('monthly')) return 'Mensuelle';
                return 'Mensuelle';
            };

            const contractRequestDTO = {
                startDate: currentDate,
                endDate: endDate.toISOString().split('T')[0],
                contractStatus: "PENDING_SIGNATURE",
                clientSignDate: null,
                dhsSignDate: null,
                contractDocClientSigned: "",
                professionalId: professional?.id,
                clientId: eoi.client?.id || eoi.clientId,
                clientEOIId: eoi.id,
                serviceFee: eoi.serviceFee?.toString() || "0",
                contractDuration: eoi.contractDuration?.toString() || "1",
                serviceName: serviceName,
                subServiceName: subServiceName,
                frequency: getFrequency(),
                serviceLocation: eoi.serviceLocation ||
                    `${eoi.client?.user?.city || ''}, ${eoi.client?.user?.country || ''}`.trim(),
                createdBy: username,
                lastModifiedBy: username
            };

            await ContractService.createContract(contractRequestDTO);
            return true;
        } catch (error) {
            console.error('Error creating contract:', error);
            throw error;
        }
    };

    const handleApprove = async () => {
        if (!eoi || !currentUser) {
            Swal.fire('Erreur', 'Données manquantes pour la validation', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Confirmer l\'approbation',
            html: `
                <div class="text-start">
                    <p>Êtes-vous sûr de vouloir approuver cette demande client ?</p>
                    <div class="alert alert-success mt-3">
                        <strong>Information:</strong> Cette action approuvera la demande et créera automatiquement un contrat.
                    </div>
                    <div class="mt-3">
                        <strong>Détails de la demande:</strong><br/>
                        • Client: ${eoi.client?.user?.firstName || 'Non'} ${eoi.client?.user?.lastName || 'spécifié'}<br/>
                        • Professionnel: ${eoi.professional?.user?.firstName} ${eoi.professional?.user?.lastName}<br/>
                        • Service: ${eoi.serviceName || eoi.professional?.selectedService}<br/>
                        • Durée: ${eoi.contractDuration || 1} mois<br/>
                        • Tarif: ${formatCurrency(eoi.serviceFee || 0)}
                    </div>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui, approuver et créer le contrat',
            cancelButtonText: 'Annuler'
        });

        if (!result.isConfirmed) return;

        setIsApproving(true);
        try {
            await createContractFromEOI(eoi);

            const updateData = {
                comments: comments || "Demande validée avec succès - Contrat créé",
                clientEOIStatus: "APPROVED",
                userValidated: currentUser.username,
                lastModifiedBy: currentUser.username,
                clientId: eoi.client?.id || eoi.clientId,
                professionalId: eoi.professional?.id || eoi.professionalId,
            };

            const updatedEOI = await ClientEOIService.updateClientEOI(eoi.id, updateData);
            onUpdate(updatedEOI);

            Swal.fire('Succès !', 'Demande approuvée et contrat créé avec succès.', 'success');
            onClose();
        } catch (error) {
            console.error('Error approving EOI:', error);
            Swal.fire('Erreur !', error.message || 'Une erreur est survenue lors de la validation.', 'error');
        } finally {
            setIsApproving(false);
        }
    };

    const handleReject = async () => {
        if (!eoi || !currentUser) {
            Swal.fire('Erreur', 'Données manquantes pour le rejet', 'error');
            return;
        }

        if (!rejectionReason.trim()) {
            Swal.fire('Erreur', 'Veuillez saisir le motif du rejet.', 'error');
            return;
        }

        setIsRejecting(true);
        try {
            const updateData = {
                comments: comments || "Demande rejetée",
                clientEOIStatus: "REJECTED",
                userRejected: currentUser.username,
                rejectionReason: rejectionReason,
                lastModifiedBy: currentUser.username,
                clientId: eoi.client?.id || eoi.clientId,
                professionalId: eoi.professional?.id || eoi.professionalId,
            };

            const updatedEOI = await ClientEOIService.updateClientEOI(eoi.id, updateData);
            onUpdate(updatedEOI);
            Swal.fire('Succès !', 'La demande a été rejetée avec succès.', 'success');
            setShowRejectModal(false);
            setRejectionReason('');
            onClose();
        } catch (error) {
            console.error('Error rejecting EOI:', error);
            Swal.fire('Erreur !', 'Une erreur est survenue lors du rejet.', 'error');
        } finally {
            setIsRejecting(false);
        }
    };

    const openRejectModal = () => {
        setShowRejectModal(true);
        setRejectionReason(eoi.rejectionReason || '');
    };

    const closeRejectModal = () => {
        setShowRejectModal(false);
        setRejectionReason('');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Badge bg="warning" className="rounded-pill px-3 py-2"><FaSyncAlt className="me-1" /> En attente</Badge>;
            case 'APPROVED':
                return <Badge bg="success" className="rounded-pill px-3 py-2"><FaCheckCircle className="me-1" /> Accepté</Badge>;
            case 'REJECTED':
                return <Badge bg="danger" className="rounded-pill px-3 py-2"><FaTimesCircle className="me-1" /> Rejeté</Badge>;
            default:
                return <Badge bg="secondary" className="rounded-pill px-3 py-2">Inconnu</Badge>;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            if (dateString.includes('T')) {
                const date = new Date(dateString);
                return date.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            } else {
                const [datePart, timePart] = dateString.split(' ');
                const [day, month, year] = datePart.split('-');
                const date = new Date(year, month - 1, day);
                return date.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }
        } catch (error) {
            return 'Date invalide';
        }
    };

    const formatDesiredStartDate = (dateData) => {
        if (!dateData) return 'Non spécifiée';

        try {
            let date;
            if (Array.isArray(dateData)) {
                const [year, month, day] = dateData;
                date = new Date(year, month - 1, day);
            } else if (typeof dateData === 'string') {
                if (dateData.includes('T')) {
                    date = new Date(dateData);
                } else if (dateData.includes('-')) {
                    const [year, month, day] = dateData.split('-');
                    date = new Date(year, month - 1, day);
                }
            } else if (dateData instanceof Date) {
                date = dateData;
            } else {
                return 'Format non supporté';
            }

            if (isNaN(date.getTime())) return 'Date invalide';
            return date.toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } catch (error) {
            return 'Erreur';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount) + ' FCFA';
    };

    return {
        isEditing,
        setIsEditing,
        comments,
        setComments,
        isLoading,
        isApproving,
        isRejecting,
        showRejectModal,
        rejectionReason,
        setRejectionReason,
        lightboxOpen,
        lightboxImages,
        lightboxIndex,
        currentUser,
        getStatusBadge,
        formatDate,
        formatDesiredStartDate,
        formatCurrency,
        getMediaUrl,
        prepareLightboxImages,
        handleSave,
        handleApprove,
        handleReject,
        openRejectModal,
        closeRejectModal,
        handleOpenLightbox,
        handleCloseLightbox,
        onCloseHandler
    };
};

export default useClientEOIDetail2;